import type { OfflineOperationType } from './offlineDb';
import { enqueue } from './offlineDb';
import type { User } from '../types';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';
const SESSION_KEY = 'mrwash-session';

export interface Session {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
  user: User;
}

export class ApiError extends Error {
  constructor(message: string, public status: number, public details?: unknown) {
    super(message);
  }
}

export function readSession(): Session | null {
  const raw = localStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw) as Session; } catch { return null; }
}

export function saveSession(session: Session | null): void {
  if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  else {
    localStorage.removeItem(SESSION_KEY);
    void clearProtectedCaches();
  }
  window.dispatchEvent(new CustomEvent('mrwash-session-changed'));
}

let refreshPromise: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  const current = readSession();
  if (!current?.refreshToken) return false;
  try {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: current.refreshToken })
    });
    if (!response.ok) throw new Error('refresh failed');
    saveSession(await response.json() as Session);
    return true;
  } catch {
    saveSession(null);
    return false;
  }
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;
  const contentType = response.headers.get('content-type') || '';
  const body = contentType.includes('application/json') ? await response.json() : await response.text();
  if (!response.ok) {
    const message = typeof body === 'object' && body && 'error' in body ? String((body as { error: unknown }).error) : `Fehler ${response.status}`;
    throw new ApiError(message, response.status, body);
  }
  return body as T;
}

export async function apiFetch<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const session = readSession();
  const headers = new Headers(init.headers);
  if (!(init.body instanceof FormData) && init.body !== undefined) headers.set('Content-Type', 'application/json');
  if (session?.accessToken) headers.set('Authorization', `Bearer ${session.accessToken}`);
  const response = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (response.status === 401 && retry && session?.refreshToken) {
    refreshPromise ??= refreshSession().finally(() => { refreshPromise = null; });
    if (await refreshPromise) return apiFetch<T>(path, init, false);
  }
  return parseResponse<T>(response);
}

export async function login(email: string, password: string): Promise<Session> {
  const response = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  });
  const session = await parseResponse<Session>(response);
  saveSession(session);
  return session;
}

export async function logout(): Promise<void> {
  const session = readSession();
  if (session) {
    await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: session.refreshToken })
    }).catch(() => undefined);
  }
  saveSession(null);
}

export async function createOfflineCapable<T>(
  path: string,
  type: OfflineOperationType,
  payload: Record<string, unknown>
): Promise<{ queued: true; operationId: string } | { queued: false; data: T }> {
  const body = { ...payload, clientMutationId: String(payload.clientMutationId || crypto.randomUUID()) };
  if (!navigator.onLine) {
    const operation = await enqueue(type, body, readSession()?.user.id || 'unknown');
    return { queued: true, operationId: operation.id };
  }
  try {
    const data = await apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) });
    return { queued: false, data };
  } catch (error) {
    if (error instanceof TypeError) {
      const operation = await enqueue(type, body, readSession()?.user.id || 'unknown');
      return { queued: true, operationId: operation.id };
    }
    throw error;
  }
}

export async function uploadAttachment(file: File, query: Record<string, string>): Promise<unknown> {
  const form = new FormData();
  form.append('file', file);
  const params = new URLSearchParams(query);
  return apiFetch(`/attachments?${params}`, { method: 'POST', body: form });
}

export async function fetchProtectedBlob(path: string): Promise<Blob> {
  const session = readSession();
  let response = await fetch(`${API_BASE}${path}`, { headers: session ? { Authorization: `Bearer ${session.accessToken}` } : {} });
  if (response.status === 401 && await refreshSession()) {
    const updated = readSession();
    response = await fetch(`${API_BASE}${path}`, { headers: updated ? { Authorization: `Bearer ${updated.accessToken}` } : {} });
  }
  if (!response.ok) throw new ApiError('Datei konnte nicht geladen werden.', response.status);
  return response.blob();
}

export async function openProtectedFile(path: string, filename?: string): Promise<void> {
  const blob = await fetchProtectedBlob(path);
  const url = URL.createObjectURL(blob);
  if (filename) {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function clearProtectedCaches(): Promise<void> {
  if (!('caches' in window)) return;
  await Promise.all(['mrwash-api', 'mrwash-images'].map((name) => caches.delete(name)));
}
